import { DatePickerInput } from '@creditornot/cb-date-picker';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { MultiSelect, Select } from '@creditornot/cb-select';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Category, ProductHierarchyLevel } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';
import TimePicker from 'common/components/weekly-schedules/TimePairInput/TimePicker';
import { localize } from 'common/utils';
import { MultiLevelMultiSelect } from 'modules/menu/components/MultiLevelSelect';
import { WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';

type Props = {
  categories: Category[];
  productFamilies: ProductHierarchyLevel[];
  language: Language;
};

export type RestockingRuleFormValues = {
  categoryIds: string[];
  productFamilyIds: string[];
  type: 'REMINDER' | 'AUTO_RESTOCKING';
  frequency: 'DAILY' | 'WEEKLY' | 'EVERY_TWO_WEEKS' | 'EVERY_THREE_WEEKS' | 'EVERY_FOUR_WEEKS' | 'SOLD_OUT_FOR_X_DAY';
  weekdays: ('SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY')[];
  applyAfterDays: number | undefined;
  timeInMinutes: number;
  startDate: Date;
  ruleName: string;
};

const StyledInputContainer = styled(InputContainer)``;

const FieldWrapper = styled.div`
  display: flex;
  margin-block-start: 12px;

  ${StyledInputContainer} {
    width: 100%;
  }

  ${StyledInputContainer} + ${StyledInputContainer} {
    width: 50%;
    margin-inline-start: 8px;
  }
`;

const createCategoryOption = (category: Category, parentId: null | string, primaryLanguage: Language) => ({
  value: category.id,
  label: localize(category.name, primaryLanguage),
  parentId,
  childrenIds: category.child_categories.map(subcategory => subcategory.id),
});

const FREQUENCY_TRANSLATION_MAP: Record<RestockingRuleFormValues['frequency'], string> = {
  DAILY: 'menu.restockingRules.ruleForm.frequencyOption.daily',
  WEEKLY: 'menu.restockingRules.ruleForm.frequencyOption.weekly',
  EVERY_TWO_WEEKS: 'menu.restockingRules.ruleForm.frequencyOption.everyTwoWeeks',
  EVERY_THREE_WEEKS: 'menu.restockingRules.ruleForm.frequencyOption.everyThreeWeeks',
  EVERY_FOUR_WEEKS: 'menu.restockingRules.ruleForm.frequencyOption.everyFourWeeks',
  SOLD_OUT_FOR_X_DAY: 'menu.restockingRules.ruleForm.frequencyOption.soldOutFor',
};

const WEEKDAY_TRANSLATION_MAP: Record<RestockingRuleFormValues['weekdays'][number], string> = {
  MONDAY: 'menu.restockingRules.ruleForm.weekdaysOption.monday',
  TUESDAY: 'menu.restockingRules.ruleForm.weekdaysOption.tuesday',
  WEDNESDAY: 'menu.restockingRules.ruleForm.weekdaysOption.wednesday',
  THURSDAY: 'menu.restockingRules.ruleForm.weekdaysOption.thursday',
  FRIDAY: 'menu.restockingRules.ruleForm.weekdaysOption.friday',
  SATURDAY: 'menu.restockingRules.ruleForm.weekdaysOption.saturday',
  SUNDAY: 'menu.restockingRules.ruleForm.weekdaysOption.sunday',
};

const WEEKDAY_FREQUENCIES: RestockingRuleFormValues['frequency'][] = [
  'WEEKLY',
  'EVERY_TWO_WEEKS',
  'EVERY_THREE_WEEKS',
  'EVERY_FOUR_WEEKS',
];

const RestockingRuleFields = ({ categories, productFamilies, language }: Props) => {
  const intl = useIntl();
  const { values } = useFormState<RestockingRuleFormValues>();

  const categoryOptions = useMemo(
    () =>
      categories
        .filter(category => category.id !== WOLT_SPECIALS_CATEGORY_ID)
        .flatMap(category => [
          createCategoryOption(category, null, language),
          ...category.child_categories.map(subcategory => createCategoryOption(subcategory, category.id, language)),
        ]),
    [categories, language],
  );

  const productHierarchyOptions = useMemo(
    () =>
      productFamilies
        .map((level, _, array) => ({
          value: level.id,
          label: localize(level.name, intl.locale),
          secondaryContent: level.human_readable_id,
          parentId: level.parent_category_id,
          childrenIds: array.filter(item => item.parent_category_id === level.id).map(item => item.id),
          tags: level.tags,
        }))
        .sort((a, b) => Number(a.secondaryContent) - Number(b.secondaryContent)) || [],
    [intl.locale, productFamilies],
  );

  const frequencyOptions = useMemo(
    () =>
      Object.entries(FREQUENCY_TRANSLATION_MAP)
        .map(([frequency, keyId]) => ({
          value: frequency,
          label: intl.formatMessage({ id: keyId }),
        }))
        // TODO: Enable SOLD_OUT_FOR_X_DAY after testing
        .filter(({ value }) => value !== 'SOLD_OUT_FOR_X_DAY'),
    [intl],
  );

  const weekDayOptions = useMemo(
    () =>
      Object.entries(WEEKDAY_TRANSLATION_MAP).map(([weekDay, keyId]) => ({
        value: weekDay,
        label: intl.formatMessage({ id: keyId }),
      })),
    [intl],
  );

  return (
    <>
      <FormRow horizontalPadding label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.chooseCategories' })}>
        <Field<RestockingRuleFormValues['categoryIds']> name="categoryIds">
          {field => (
            <MultiLevelMultiSelect
              placeholder={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.selectOrSearch' })}
              options={categoryOptions}
              {...field.input}
              values={field.input.value}
            />
          )}
        </Field>
      </FormRow>
      <FormRow
        horizontalPadding
        label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.chooseProductFamilies' })}
      >
        <Field<RestockingRuleFormValues['productFamilyIds']> name="productFamilyIds">
          {field => (
            <MultiLevelMultiSelect
              placeholder={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.selectOrSearch' })}
              {...field.input}
              options={productHierarchyOptions}
              values={field.input.value}
            />
          )}
        </Field>
      </FormRow>
      <FormRow required horizontalPadding label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.ruleName' })}>
        <Field<RestockingRuleFormValues['ruleName']> name="ruleName">
          {field => <RFFFieldErrorWrapper field={field}>{input => <Input {...input} />}</RFFFieldErrorWrapper>}
        </Field>
      </FormRow>
      <FormRow horizontalPadding label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.type' })}>
        {/* TODO: Enabled both REMINDER and AUTO_RESTOCKING after testing*/}
        {/* <Field<RestockingRuleFormValues['type']> name="type">
          {field => (
            <RadioGroup>
              <StyledRadio
                checked={field.input.value === 'REMINDER'}
                onChange={() => field.input.onChange('REMINDER')}
                onBlur={field.input.onBlur}
              >
                {intl.formatMessage({ id: 'menu.restockingRules.ruleForm.reminder' })}
              </StyledRadio>
              <StyledRadio
                checked={field.input.value === 'AUTO_RESTOCKING'}
                onChange={() => field.input.onChange('AUTO_RESTOCKING')}
                onBlur={field.input.onBlur}
              >
                {intl.formatMessage({ id: 'menu.restockingRules.ruleForm.autoRestock' })}
                </StyledRadio>
                </RadioGroup>
                )}
              </Field> */}
        {intl.formatMessage({ id: 'menu.restockingRules.ruleForm.autoRestock' })}
      </FormRow>
      <FormRow horizontalPadding label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.details' })}>
        <Field<RestockingRuleFormValues['frequency']> name="frequency">
          {frequencyField => (
            <>
              <InputContainer label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.frequency' })}>
                <Select {...frequencyField.input} options={frequencyOptions} disableSearch menuPlacement="top" />
              </InputContainer>

              {WEEKDAY_FREQUENCIES.includes(values.frequency) && (
                <FieldWrapper>
                  <StyledInputContainer
                    label={intl.formatMessage({
                      id:
                        values.type === 'REMINDER'
                          ? 'menu.restockingRules.ruleForm.weekdays'
                          : 'menu.restockingRules.ruleForm.autoRestockDay',
                    })}
                  >
                    <Field<RestockingRuleFormValues['weekdays']> name="weekdays">
                      {weekdaysField => (
                        <RFFFieldErrorWrapper field={weekdaysField}>
                          {input => (
                            <MultiSelect {...input} disableSearch options={weekDayOptions} menuPlacement="top" />
                          )}
                        </RFFFieldErrorWrapper>
                      )}
                    </Field>
                  </StyledInputContainer>
                </FieldWrapper>
              )}
              {values.frequency === 'SOLD_OUT_FOR_X_DAY' && (
                <FieldWrapper>
                  <StyledInputContainer
                    label={intl.formatMessage({
                      id:
                        values.type === 'REMINDER'
                          ? 'menu.restockingRules.ruleForm.reminderAfter'
                          : 'menu.restockingRules.ruleForm.autoRestockAfter',
                    })}
                  >
                    <Field<RestockingRuleFormValues['applyAfterDays']> name="applyAfterDays">
                      {applyAfterDaysField => (
                        <RFFFieldErrorWrapper field={applyAfterDaysField}>
                          {input => <Input type="number" min={0} {...input} suffixText={'Day(s)'} />}
                        </RFFFieldErrorWrapper>
                      )}
                    </Field>
                  </StyledInputContainer>
                </FieldWrapper>
              )}
              <FieldWrapper>
                <Field<RestockingRuleFormValues['timeInMinutes']> name="timeInMinutes">
                  {field => (
                    <StyledInputContainer
                      label={intl.formatMessage({
                        id:
                          values.type === 'REMINDER'
                            ? 'menu.restockingRules.ruleForm.reminderTime'
                            : 'menu.restockingRules.ruleForm.autoRestockTime',
                      })}
                    >
                      <TimePicker {...field.input} menuPlacement="top" />
                    </StyledInputContainer>
                  )}
                </Field>
                {values.frequency !== 'SOLD_OUT_FOR_X_DAY' && (
                  <Field<RestockingRuleFormValues['startDate']> name="startDate">
                    {startDateField => (
                      <StyledInputContainer
                        label={intl.formatMessage({ id: 'menu.restockingRules.ruleForm.startDate' })}
                      >
                        <DatePickerInput
                          {...startDateField.input}
                          disabledDays={{
                            before: DateTime.now().toJSDate(),
                          }}
                          disableClear
                          // Ensure dropdown stay on top of wolt-modal
                          popoverZIndex={99999999}
                        />
                      </StyledInputContainer>
                    )}
                  </Field>
                )}
              </FieldWrapper>
            </>
          )}
        </Field>
      </FormRow>
    </>
  );
};

export default RestockingRuleFields;
